import { useState, useEffect, useRef } from "react";

/* ── Google Fonts injected once ── */
const FontLoader = () => {
  useEffect(() => {
    const link = document.createElement("link");
    link.href =
      "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=DM+Mono:wght@300;400&family=Bebas+Neue&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }, []);
  return null;
};

/* ── Inline styles as JS objects ── */
const COLORS = {
  red:     "#c0152a",
  redDark: "#8b0000",
  redPale: "#fdf0f0",
  border:  "rgba(192,21,42,0.15)",
  ink:     "#1a0505",
  white:   "#ffffff",
  muted:   "#9a6060",
};

const fonts = {
  display: "'Bebas Neue', sans-serif",
  body:    "'Cormorant Garamond', Georgia, serif",
  mono:    "'DM Mono', monospace",
};

/* ── Reusable animated section hook ── */
function useFadeIn(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

/* ── Checkmark SVG ── */
const Check = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
    <circle cx="10" cy="10" r="9" stroke={COLORS.red} strokeWidth="1.5" fill={COLORS.redPale} />
    <path d="M5.5 10.5l3 3 6-6" stroke={COLORS.red} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* ── TOOLS DATA ── */
const toolGroups = [
  {
    title: "We Help You Accomplish Your Goals",
    items: [
      "Manage Multiple Lists of Contacts",
      "Create Professional Looking Articles and Events",
      "Send Emails & Newsletters",
      "Connects to Your Social Media",
    ],
  },
  {
    title: "Making Your Life Easier",
    items: [
      "Multiple Administrators Can Be Added to Your Team",
      "Automatic Email Collection Widget for Your Website",
      "Consistent Design and Preview Options",
      "Import and Export Features",
    ],
  },
];

/* ══════════════════════════════════════
   NAV
══════════════════════════════════════ */
function Nav() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  return (
    <nav style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
      display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "1.1rem 3rem",
      background: scrolled ? "rgba(255,255,255,0.96)" : "rgba(255,255,255,0.9)",
      backdropFilter: "blur(12px)",
      borderBottom: `1px solid ${COLORS.border}`,
      transition: "background 0.3s, box-shadow 0.3s",
      boxShadow: scrolled ? "0 2px 20px rgba(192,21,42,0.08)" : "none",
    }}>
      <a href="http://www.sdsan.org" style={{
        fontFamily: fonts.mono, fontSize: "0.72rem", letterSpacing: "0.2em",
        textTransform: "uppercase", color: COLORS.ink, textDecoration: "none",
      }}>
        SDSAN · Community Platform
      </a>
      <div>
        <SignupButton small />
        <LoginButton small />
      </div>
    </nav>
  );
}

/* ══════════════════════════════════════
   LOGIN BUTTON
══════════════════════════════════════ */
function LoginButton({ small = false }) {
  const [hovered, setHovered] = useState(false);
  const base = {
    display: "inline-flex", flexDirection: "column", alignItems: "center",
    gap: "0.2rem", background: hovered ? COLORS.redDark : COLORS.red,
    color: COLORS.white, textDecoration: "none",
    padding: small ? "0.5rem 1.3rem" : "1.5rem 4rem",
    transition: "background 0.25s, transform 0.2s, box-shadow 0.3s",
    transform: hovered && !small ? "translateY(-3px)" : "translateY(0)",
    boxShadow: hovered && !small
      ? "0 10px 36px rgba(192,21,42,0.38)"
      : small ? "none" : "0 4px 24px rgba(192,21,42,0.22)",
    cursor: "pointer", position: "relative", overflow: "hidden",
  };
  return (
    <a href="/login" style={base}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {!small && (
        <span style={{ fontFamily: fonts.mono, fontSize: "0.6rem", letterSpacing: "0.28em", textTransform: "uppercase", opacity: 0.7 }}>
          SDSAN Community Platform
        </span>
      )}
      <span style={{ fontFamily: fonts.display, fontSize: small ? "0.9rem" : "2rem", letterSpacing: "0.14em" }}>
        {small ? "Log In" : "LOG IN"}
      </span>
      {!small && (
        <span style={{ fontSize: "0.85rem", opacity: 0.6, transition: "transform 0.2s", transform: hovered ? "translateX(5px)" : "none" }}>
          →
        </span>
      )}
    </a>
  );
}

function SignupButton({ small = false }) {
  const [hovered, setHovered] = useState(false);
  const base = {
    display: "inline-flex", flexDirection: "column", alignItems: "center",
    gap: "0.2rem", background: hovered ? COLORS.redDark : COLORS.red,
    color: COLORS.white, textDecoration: "none",
    marginRight: "5px",
    padding: small ? "0.5rem 1.3rem" : "1.5rem 4rem",
    transition: "background 0.25s, transform 0.2s, box-shadow 0.3s",
    transform: hovered && !small ? "translateY(-3px)" : "translateY(0)",
    boxShadow: hovered && !small
      ? "0 10px 36px rgba(192,21,42,0.38)"
      : small ? "none" : "0 4px 24px rgba(192,21,42,0.22)",
    cursor: "pointer", position: "relative", overflow: "hidden",
  };
  return (
    <a href="/signup" style={base}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {!small && (
        <span style={{ fontFamily: fonts.mono, fontSize: "0.6rem", letterSpacing: "0.28em", textTransform: "uppercase", opacity: 0.7 }}>
          SDSAN Community Platform
        </span>
      )}
      <span style={{ fontFamily: fonts.display, fontSize: small ? "0.9rem" : "2rem", letterSpacing: "0.14em" }}>
        {small ? "Sign Up" : "SIGN UP"}
      </span>
      {!small && (
        <span style={{ fontSize: "0.85rem", opacity: 0.6, transition: "transform 0.2s", transform: hovered ? "translateX(5px)" : "none" }}>
          →
        </span>
      )}
    </a>
  );
}

/* ══════════════════════════════════════
   HERO
══════════════════════════════════════ */
const heroKeyframes = `
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(24px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes pulseLine {
    0%,100% { opacity: 0.4; }
    50%      { opacity: 1; }
  }
`;

function Hero() {
  return (
    <>
      <style>{heroKeyframes}</style>
      <section id="login" style={{
        minHeight: "100vh",
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        textAlign: "center", padding: "8rem 2rem 6rem",
        position: "relative", overflow: "hidden",
        background: `
          radial-gradient(ellipse at 50% 0%, rgba(192,21,42,0.07) 0%, transparent 65%),
          radial-gradient(ellipse at 20% 100%, rgba(139,0,0,0.05) 0%, transparent 50%),
          #fff`,
      }}>
        {/* Decorative rings */}
        {[720, 520, 330].map((size, i) => (
          <div key={i} style={{
            position: "absolute", borderRadius: "50%",
            border: `1px solid rgba(192,21,42,${0.07 + i * 0.03})`,
            width: size, height: size,
            top: "50%", left: "50%",
            transform: "translate(-50%, -50%)",
            pointerEvents: "none",
          }} />
        ))}

        {/* Content */}
        <div style={{ position: "relative", zIndex: 1 }}>
          <p style={{
            fontFamily: fonts.mono, fontSize: "0.68rem", letterSpacing: "0.3em",
            textTransform: "uppercase", color: COLORS.red, marginBottom: "1.8rem",
            opacity: 0, animation: "fadeUp 0.8s 0.1s forwards",
          }}>
            San Diego Synergy Arts Network
          </p>

          <h1 style={{
            fontFamily: fonts.display, fontSize: "clamp(3.5rem, 9vw, 7.5rem)",
            lineHeight: 0.95, letterSpacing: "0.03em", color: COLORS.ink,
            marginBottom: "0.3rem",
            opacity: 0, animation: "fadeUp 0.8s 0.25s forwards",
          }}>
            SDSAN
          </h1>

          <p style={{
            fontFamily: fonts.display, fontSize: "clamp(1.6rem, 4vw, 3rem)",
            letterSpacing: "0.1em", color: COLORS.red, marginBottom: "2.2rem",
            opacity: 0, animation: "fadeUp 0.8s 0.35s forwards",
          }}>
            Community Platform
          </p>

          <p style={{
            fontFamily: fonts.body, fontSize: "clamp(1.1rem, 1.8vw, 1.5rem)",
            fontStyle: "italic", fontWeight: 300, color: COLORS.muted,
            maxWidth: 500, lineHeight: 1.6, margin: "0 auto 3.2rem",
            opacity: 0, animation: "fadeUp 0.8s 0.5s forwards",
          }}>
            We help organizations and communities get organized and connected.
          </p>

          <div style={{ opacity: 0, animation: "fadeUp 0.8s 0.65s forwards", display: "inline-block" }}>
            <LoginButton />
          </div>
        </div>

        {/* Scroll indicator */}
        <div style={{
          position: "absolute", bottom: "2.5rem", left: "50%",
          transform: "translateX(-50%)",
          fontFamily: fonts.mono, fontSize: "0.58rem",
          letterSpacing: "0.25em", textTransform: "uppercase",
          color: COLORS.muted, display: "flex", flexDirection: "column",
          alignItems: "center", gap: "0.6rem",
          opacity: 0, animation: "fadeUp 0.8s 1.1s forwards",
        }}>
          <span>Scroll to explore</span>
          <div style={{
            width: 1, height: 36,
            background: `linear-gradient(to bottom, ${COLORS.red}, transparent)`,
            animation: "pulseLine 2s infinite",
          }} />
        </div>
      </section>
    </>
  );
}

/* ══════════════════════════════════════
   INTRO BAND
══════════════════════════════════════ */
function IntroBand() {
  const [ref, visible] = useFadeIn();
  return (
    <div ref={ref} style={{
      background: COLORS.red, color: COLORS.white,
      padding: "5rem 3.5rem", textAlign: "center",
      transition: "opacity 0.8s, transform 0.8s",
      opacity: visible ? 1 : 0,
      transform: visible ? "none" : "translateY(20px)",
    }}>
      <p style={{
        fontFamily: fonts.body, fontSize: "clamp(1.3rem, 2.4vw, 1.9rem)",
        fontStyle: "italic", fontWeight: 300, lineHeight: 1.55,
        maxWidth: 700, margin: "0 auto",
      }}>
        All the tools you need — in one place — to accomplish your organization's day-to-day goals.
      </p>
    </div>
  );
}

/* ══════════════════════════════════════
   TOOLS SECTION
══════════════════════════════════════ */
function ToolGroup({ title, items, delay = 0 }) {
  const [ref, visible] = useFadeIn(0.1);
  return (
    <div ref={ref} style={{
      transition: `opacity 0.7s ${delay}ms, transform 0.7s ${delay}ms`,
      opacity: visible ? 1 : 0,
      transform: visible ? "none" : "translateY(24px)",
    }}>
      <h3 style={{
        fontFamily: fonts.display, fontSize: "clamp(1.3rem, 2vw, 1.75rem)",
        letterSpacing: "0.05em", color: COLORS.red,
        marginBottom: "1.8rem", paddingBottom: "0.7rem",
        borderBottom: `2px solid ${COLORS.red}`,
      }}>
        {title}
      </h3>
      <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: "1.1rem" }}>
        {items.map((item) => (
          <li key={item} style={{
            display: "flex", alignItems: "flex-start", gap: "0.9rem",
            fontFamily: fonts.body, fontSize: "clamp(1rem, 1.3vw, 1.18rem)",
            fontWeight: 300, lineHeight: 1.45, color: COLORS.ink,
          }}>
            <Check />
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Tools() {
  const [ref, visible] = useFadeIn(0.1);
  return (
    <section style={{ padding: "7rem 3.5rem", background: COLORS.white }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div ref={ref} style={{
          textAlign: "center", marginBottom: "5rem",
          transition: "opacity 0.7s, transform 0.7s",
          opacity: visible ? 1 : 0,
          transform: visible ? "none" : "translateY(20px)",
        }}>
          <span style={{
            fontFamily: fonts.mono, fontSize: "0.65rem",
            letterSpacing: "0.28em", textTransform: "uppercase",
            color: COLORS.red, display: "block", marginBottom: "1rem",
          }}>
            What We Offer
          </span>
          <h2 style={{
            fontFamily: fonts.display, fontSize: "clamp(2rem, 4vw, 3.2rem)",
            letterSpacing: "0.04em", color: COLORS.ink,
          }}>
            All the Tools You Need in One Place
          </h2>
        </div>

        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: "4rem 7rem",
        }}>
          {toolGroups.map((group, i) => (
            <ToolGroup key={group.title} {...group} delay={i * 120} />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════
   TESTIMONIAL
══════════════════════════════════════ */
function Testimonial() {
  const [ref, visible] = useFadeIn(0.1);
  return (
    <section style={{
      background: COLORS.redPale,
      borderTop: `1px solid ${COLORS.border}`,
      borderBottom: `1px solid ${COLORS.border}`,
      padding: "7rem 3.5rem",
    }}>
      <div ref={ref} style={{
        maxWidth: 780, margin: "0 auto", textAlign: "center",
        transition: "opacity 0.9s, transform 0.9s",
        opacity: visible ? 1 : 0,
        transform: visible ? "none" : "translateY(28px)",
      }}>
        <span style={{
          fontFamily: fonts.body, fontSize: "9rem", lineHeight: 0.4,
          color: COLORS.red, opacity: 0.2, display: "block", marginBottom: "1.5rem",
        }}>"</span>

        <p style={{
          fontFamily: fonts.body, fontSize: "clamp(1.15rem, 1.8vw, 1.5rem)",
          fontStyle: "italic", fontWeight: 300, lineHeight: 1.7,
          color: COLORS.ink, marginBottom: "2.5rem",
        }}>
          This Platform has made my life so much easier in{" "}
          <span style={{ color: COLORS.red, fontStyle: "normal", fontWeight: 400 }}>
            creating events, sending emails, press releases and social media
          </span>
          . We now have our contacts divided into separate mailing lists and we can{" "}
          <span style={{ color: COLORS.red, fontStyle: "normal", fontWeight: 400 }}>
            track all our statistics for grants
          </span>
          . Plus it offers lots of other features to keep me up to date.
        </p>

        <div style={{ width: 40, height: 2, background: COLORS.red, margin: "0 auto 1.8rem" }} />

        <p style={{
          fontFamily: fonts.mono, fontSize: "0.72rem",
          letterSpacing: "0.15em", textTransform: "uppercase",
          color: COLORS.ink, marginBottom: "0.5rem",
        }}>
          Patricia Frischer
        </p>
        <p style={{
          fontFamily: fonts.body, fontSize: "0.98rem",
          fontStyle: "italic", color: COLORS.muted, lineHeight: 1.6,
        }}>
          Founder, San Diego Visual Arts Network<br />
          Co-Founder, North County Arts Network
        </p>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════
   FOOTER CTA
══════════════════════════════════════ */
function FooterCTA() {
  const [ref, visible] = useFadeIn(0.1);
  return (
    <div ref={ref} style={{
      background: COLORS.ink, color: COLORS.white,
      padding: "6rem 3.5rem", textAlign: "center",
      transition: "opacity 0.8s, transform 0.8s",
      opacity: visible ? 1 : 0,
      transform: visible ? "none" : "translateY(20px)",
    }}>
      <p style={{
        fontFamily: fonts.body, fontSize: "1.1rem", fontStyle: "italic",
        color: "rgba(255,255,255,0.55)", marginBottom: "2.5rem",
      }}>
        Join organizations and communities across San Diego.
      </p>
      <LoginButton />
    </div>
  );
}

/* ══════════════════════════════════════
   FOOTER
══════════════════════════════════════ */
const footerLinks = [
  { label: "Synergy Arts Network", href: "https://www.sdsynergyarts.org" },
  { label: "Visual Arts Network",  href: "https://www.sdvisualarts.net"  },
  { label: "www.sdsan.org",        href: "http://www.sdsan.org"          },
];

function Footer() {
  return (
    <footer style={{
      padding: "2.5rem 3.5rem",
      display: "flex", justifyContent: "space-between", alignItems: "center",
      borderTop: `1px solid ${COLORS.border}`, flexWrap: "wrap", gap: "1rem",
    }}>
      <ul style={{ listStyle: "none", display: "flex", gap: "2rem", flexWrap: "wrap" }}>
        {footerLinks.map(({ label, href }) => (
          <li key={label}>
            <FooterLink href={href}>{label}</FooterLink>
          </li>
        ))}
      </ul>
      <p style={{
        fontFamily: fonts.mono, fontSize: "0.6rem",
        letterSpacing: "0.1em", color: COLORS.muted,
      }}>
        © 2025 SDSAN · 501(c)(3) Nonprofit
      </p>
    </footer>
  );
}

function FooterLink({ href, children }) {
  const [hovered, setHovered] = useState(false);
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" style={{
      fontFamily: fonts.mono, fontSize: "0.62rem",
      letterSpacing: "0.12em", textTransform: "uppercase",
      color: hovered ? COLORS.red : COLORS.muted,
      textDecoration: "none", transition: "color 0.2s",
    }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}
    </a>
  );
}

/* ══════════════════════════════════════
   ROOT APP
══════════════════════════════════════ */
export default function SDSANLanding() {
  return (
    <div style={{ fontFamily: fonts.body, background: COLORS.white, color: COLORS.ink, overflowX: "hidden" }}>
      <FontLoader />

      {/* Global reset + noise texture */}
      <style>{`
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        body::before {
          content: '';
          position: fixed; inset: 0; pointer-events: none; z-index: 9999; opacity: 0.28;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E");
        }
      `}</style>

      <Nav />
      <Hero />
      <IntroBand />
      <Tools />
      <Testimonial />
      <FooterCTA />
      <Footer />
    </div>
  );
}
